import { Button, Text } from '@cretesuite/design-system'
import {
  IonButton,
  IonButtons,
  IonContent,
  IonHeader,
  IonIcon,
  IonTitle,
  IonToolbar,
} from '@ionic/react'
import { msg } from '@lingui/core/macro'
import { useLingui } from '@lingui/react'
import { arrowBackOutline, chevronDownOutline } from 'ionicons/icons'
import { DateTime } from 'luxon'
import { useMemo, useState } from 'react'
import { useMutation, useQuery } from 'urql'
import { Input } from '../components/form/Input'
import { Textarea } from '../components/form/Textarea'
import { fieldStyles as sharedFieldStyles } from '../components/form/fieldStyles'
import {
  CancelOrderMutation,
  GetOrderCancellationReasons,
} from '../graphql/orderMutations'
import { submitQueueable } from '../helpers/submitQueueable'
import { useCurrentUser } from '../hooks/useCurrentUser'
import { useNetwork } from '../hooks/useNetwork'
import { useToast } from '../hooks/useToast'

/* ─────────────────
   Field-level styles (shared base + modal-specific select row)
   ───────────────── */
const styles = {
  ...sharedFieldStyles,
  selectRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '14px 16px',
    borderRadius: '5px',
    border: '1.5px solid rgba(0,0,0,0.1)',
    backgroundColor: 'var(--cs-color-surface, #fafafa)',
    cursor: 'pointer',
    transition: 'border-color 0.2s, background-color 0.2s',
  } as React.CSSProperties,
}

type CancelOrderModalProps = {
  orderId: number
  onDismiss: () => void
}

interface CancellationReason {
  name: string
  slug: string
  sort: number
}

export const CancelOrderModal = ({
  orderId,
  onDismiss,
}: CancelOrderModalProps) => {
  const { _ } = useLingui()
  const { isOnline } = useNetwork()
  const { user } = useCurrentUser()
  const [presentToast] = useToast()

  const [reasonsResult] = useQuery({
    query: GetOrderCancellationReasons,
    variables: { where: { active: { equals: true } } },
  })

  const [, cancelOrder] = useMutation(CancelOrderMutation)

  const reasons = useMemo((): CancellationReason[] => {
    const raw = reasonsResult.data?.orderCancellationReasons ?? []
    const list = [...raw] as CancellationReason[]
    const hasOther = list.some((r) => r.name.toLowerCase() === 'other')
    if (!hasOther) {
      list.push({
        name: 'Other',
        slug: '__other__',
        sort: Number.POSITIVE_INFINITY,
      })
    }
    return list
  }, [reasonsResult.data])

  const [who, setWho] = useState('')
  const [reason, setReason] = useState<string | undefined>()
  const [comments, setComments] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [showReasonPicker, setShowReasonPicker] = useState(false)

  const selectedReasonLabel = useMemo(() => {
    if (!reason) return ''
    return reasons.find((r) => r.slug === reason)?.name || reason
  }, [reason, reasons])

  const handleSubmit = async () => {
    if (!who.trim()) {
      setError(
        _(msg`Please enter the name of the person who cancelled the order`),
      )
      return
    }
    if (!reason) {
      setError(_(msg`Please select a cancellation reason`))
      return
    }

    setError(null)
    setSubmitting(true)

    const cancellationReason = reason === '__other__' ? undefined : reason
    const commentText = [`Order was cancelled by ${who}.`, comments]
      .filter(Boolean)
      .join('  \n\n')

    const exec = cancelOrder({
      id: orderId,
      cancellationReason: cancellationReason ?? null,
      comment: {
        personId: user?.id,
        text: commentText,
        createdAt: DateTime.utc().toISO(),
      },
    })

    // The cancel goes through updateOrder (optimistic resolver), so the order
    // flips to cancelled locally and queues. Never block the user: offline or a
    // too-slow connection → confirm and sync in the background.
    const outcome = await submitQueueable(exec, { isOnline })
    setSubmitting(false)

    if (outcome.status === 'error') {
      setError(outcome.result.error?.message ?? _(msg`An error occurred`))
      return
    }

    presentToast({
      color: 'success',
      message:
        outcome.status === 'queued'
          ? _(msg`Saved. It will sync when you're back online.`)
          : _(msg`The order has been cancelled.`),
      duration: 3000,
    })
    onDismiss()
  }

  return (
    <>
      <IonHeader>
        <IonToolbar>
          <IonButtons slot='start'>
            <IonButton onClick={onDismiss}>
              <IonIcon icon={arrowBackOutline} />
            </IonButton>
          </IonButtons>
          <IonTitle style={{ fontWeight: 700, fontSize: '17px' }}>
            {_(msg`Cancel Order`)}
          </IonTitle>
        </IonToolbar>
      </IonHeader>
      <IonContent fullscreen>
        <div style={{ padding: '0 16px 24px' }}>
          {/* ── Cancelled by ── */}
          <div style={styles.item}>
            {/* biome-ignore lint/a11y/noLabelWithoutControl: wrapping label */}
            <label style={styles.label}>
              {_(msg`Cancelled by`)}
              <Input
                variant='boxed'
                value={who}
                onChange={(e) => setWho(e.target.value)}
                placeholder={_(msg`Who authorized the cancellation?`)}
              />
            </label>
          </div>

          {/* ── Reason ── */}
          <div style={styles.item}>
            <span style={styles.label}>{_(msg`Reason`)}</span>
            <button
              type='button'
              style={{
                ...styles.buttonReset,
                ...styles.selectRow,
              }}
              onClick={() => setShowReasonPicker(!showReasonPicker)}
            >
              <span
                style={{
                  fontSize: '16px',
                  fontWeight: reason ? 500 : 400,
                  color: reason
                    ? 'var(--cs-color-text, #1a1a1a)'
                    : 'var(--cs-color-text-secondary, #999)',
                }}
              >
                {reason
                  ? selectedReasonLabel
                  : _(msg`Select cancellation reason`)}
              </span>
              <IonIcon
                icon={chevronDownOutline}
                style={{
                  fontSize: '18px',
                  color: 'var(--cs-color-text-secondary, #999)',
                  transition: 'transform 0.2s',
                  transform: showReasonPicker ? 'rotate(180deg)' : 'rotate(0)',
                }}
              />
            </button>
            {showReasonPicker && (
              <div
                style={{
                  marginTop: '8px',
                  borderRadius: '5px',
                  border: '1.5px solid rgba(0,0,0,0.08)',
                  backgroundColor: 'var(--cs-color-surface, #fff)',
                  overflow: 'hidden',
                  boxShadow: '0 2px 12px rgba(0,0,0,0.06)',
                }}
              >
                {reasons.map((r) => (
                  <button
                    type='button'
                    key={r.slug}
                    onClick={() => {
                      setReason(r.slug)
                      setShowReasonPicker(false)
                    }}
                    style={{
                      ...styles.buttonReset,
                      padding: '12px 16px',
                      fontSize: '15px',
                      fontWeight: reason === r.slug ? 600 : 400,
                      color:
                        reason === r.slug
                          ? 'var(--ion-color-primary, #3880ff)'
                          : 'var(--cs-color-text, #1a1a1a)',
                      backgroundColor:
                        reason === r.slug
                          ? 'rgba(56,128,255,0.05)'
                          : 'transparent',
                      borderBottom: '1px solid rgba(0,0,0,0.05)',
                      transition: 'background-color 0.15s',
                    }}
                  >
                    {r.name}
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* ── Comments ── */}
          <div style={styles.item}>
            <span style={styles.label}>{_(msg`Comments`)}</span>
            <Textarea
              variant='boxed'
              value={comments}
              onChange={(e) => setComments(e.target.value)}
              placeholder={_(msg`Additional info regarding the cancellation`)}
              rows={3}
            />
          </div>

          {/* ── Error ── */}
          {error && (
            <div style={{ padding: '8px 0' }}>
              <Text
                variant='label'
                style={{
                  color: 'var(--ion-color-danger, #eb445a)',
                  fontSize: '13px',
                  fontWeight: 600,
                }}
              >
                {error}
              </Text>
            </div>
          )}

          {/* ── Submit ── */}
          <div style={{ marginTop: '24px' }}>
            <Button
              onClick={handleSubmit}
              disabled={submitting}
              variant='primary'
              style={{
                width: '100%',
                borderRadius: '5px',
                padding: '16px',
                fontSize: '16px',
                fontWeight: 700,
              }}
            >
              {submitting ? _(msg`Cancelling...`) : _(msg`Cancel Order`)}
            </Button>
          </div>
        </div>
      </IonContent>
    </>
  )
}
